/**
 * Firestore writes — the user's own record, and their assessment history.
 *
 * Two collections, two different rules, for two different reasons:
 *
 *   users/{uid}                  read/write by the owner. Mutable — consent can
 *                                be withdrawn, and a preference you cannot
 *                                change is not a preference.
 *   users/{uid}/assessments/{id} create-only. Append-only is enforced by the
 *                                DATABASE, not by our discipline (R13, ADR-022):
 *                                the delta over time is the product, and a
 *                                history that can be quietly rewritten is not a
 *                                history.
 *
 * Nothing here writes a name, an employer, or contact details. What lands in
 * Firestore is the shape of someone's work, never their identity beyond the uid
 * Firebase already holds (ADR-024).
 */
import {
  doc, setDoc, addDoc, collection, serverTimestamp, getDocs, query, orderBy, limit,
} from "firebase/firestore";
import { db } from "./firebase";

/**
 * Newsletter consent.
 *
 * AI Signals is still published on LinkedIn, which offers no API to subscribe
 * anyone programmatically. So this records the CONSENT and nothing else — it
 * does not add anyone to a list, because we cannot, and a checkbox that claims
 * to do something it does not do is worse than no checkbox.
 *
 * When the list moves to a real ESP, this record is the migration: everyone who
 * ticked it, with the date and the wording they agreed to. Storing the wording
 * matters — consent is to a specific statement, and "they ticked something once"
 * is not a defensible record of what they agreed to.
 */
export const NEWSLETTER_CONSENT_TEXT =
  "Send me AI Signals — the ideas behind this analysis, roughly weekly.";

export async function saveProfile(uid, { newsletterOptIn, personaHint } = {}) {
  if (!db || !uid) return { ok: false, reason: "no-db" };
  try {
    await setDoc(
      doc(db, "users", uid),
      {
        updatedAt: serverTimestamp(),
        ...(personaHint ? { personaHint } : {}),
        ...(newsletterOptIn === undefined
          ? {}
          : {
              newsletter: {
                optIn: Boolean(newsletterOptIn),
                consentText: NEWSLETTER_CONSENT_TEXT,
                at: serverTimestamp(),
                source: "pathway-wall",
                delivered: false, // no ESP yet — see the note above
              },
            }),
      },
      { merge: true }
    );
    return { ok: true };
  } catch (e) {
    console.error("[profile] saveProfile", e?.code || e);
    return { ok: false, reason: e?.code || "write-failed" };
  }
}

/**
 * One dated assessment. Called once per completed diagnosis.
 *
 * Deliberately narrow: the occupation we landed on, how sure we were, the four
 * axis scores, and which activities the person confirmed. Enough to show
 * someone what moved since last time; not enough to reconstruct their CV.
 */
export async function saveAssessment(uid, { occupationCode, occupationTitle, confidence,
                                            resolvedVia, exposure, confirmed = [] } = {}) {
  if (!db || !uid) return { ok: false, reason: "no-db" };
  try {
    const ref = await addDoc(collection(db, "users", uid, "assessments"), {
      at: serverTimestamp(),
      occupationCode: occupationCode || null,
      occupationTitle: occupationTitle || null,
      confidence: typeof confidence === "number" ? confidence : null,
      resolvedVia: resolvedVia || null,
      exposure: exposure
        ? {
            overall: exposure.exposure,
            digitality: exposure.digitality,
            judgment: exposure.judgment,
            coordination: exposure.coordination,
            premium: exposure.premium,
            premiumWarnings: exposure.premiumWarnings ?? 0,
            method: exposure.method || null,
            personalised: exposure.personalised ?? 0,
          }
        : null,
      confirmedActivities: confirmed.slice(0, 12),
      schema: 1,
    });
    return { ok: true, id: ref.id };
  } catch (e) {
    console.error("[profile] saveAssessment", e?.code || e);
    return { ok: false, reason: e?.code || "write-failed" };
  }
}

/** Prior assessments, newest first — the raw material for "what moved". */
export async function loadHistory(uid, n = 12) {
  if (!db || !uid) return [];
  try {
    const snap = await getDocs(
      query(collection(db, "users", uid, "assessments"), orderBy("at", "desc"), limit(n))
    );
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (e) {
    console.error("[profile] loadHistory", e?.code || e);
    return [];
  }
}
